<script setup lang="ts">
/**
 * Schmale Fussleiste am unteren Rand.
 *
 * Sie existiert, damit Impressum und Datenschutz von jeder Stelle aus
 * erreichbar sind. DREHRAUM kennt sonst keine Unterseiten und haette dafuer
 * keinen Ort; ein Menue waere eines zu viel. Die Leiste nimmt der Anwendung
 * ihre Hoehe (--footer-h) und stellt dafuer nichts in den Weg.
 *
 * Uebernommen aus dem Schwesterprojekt gene, auf die Farben von DREHRAUM
 * umgestellt: dunkler Grund ist hier die Textfarbe, helle Tinte der
 * Seitengrund.
 */
import { RouterLink } from 'vue-router';

const jahr = new Date().getFullYear();

/**
 * Der Stand, auf dem die Seite steht.
 *
 * Zur Bauzeit aus `package.json` eingesetzt (`vite.config.ts`), nicht zur
 * Laufzeit geholt: die Angabe soll sagen, welches Buendel gerade ausgeliefert
 * wird, und dafuer muss sie *in* diesem Buendel stehen. Geschrieben wird sie
 * von `npm version` und nirgends von Hand.
 *
 * Hier stand bis dahin die Signatur `0xFFFEFELDMANN`. Sie ist nicht verloren
 * -- sie sitzt weiter auf dem Orakelblatt, in `Wortmarke.vue`, wo sie sich
 * auf die Wortmarke bezieht und mit ihr dreht. In der Fussleiste bezog sie
 * sich auf nichts; die Ecke traegt jetzt eine Auskunft statt eines Scherzes.
 */
const version = __VERSION__;
</script>

<template>
  <footer class="site-footer">
    <span class="site-footer__mark">DREHRAUM</span>

    <nav class="site-footer__legal" aria-label="Rechtliches">
      <span class="site-footer__copyright"
        >© {{ jahr }}
        <a class="site-footer__link" href="https://lukasbaumert.de" target="_blank" rel="noopener"
          >Lukas Baumert</a
        ></span
      >
      <span aria-hidden="true">·</span>
      <RouterLink class="site-footer__link" to="/impressum">Impressum</RouterLink>
      <span aria-hidden="true">·</span>
      <RouterLink class="site-footer__link" to="/datenschutz">Datenschutz</RouterLink>
    </nav>

    <span class="site-footer__version" :title="`Stand ${version}`">v{{ version }}</span>
  </footer>
</template>

<style scoped>
.site-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: var(--footer-h);
  /* Ueber dem Seiteninhalt, aber unter den Modalen -- die sind Fenster, die
     man selbst geoeffnet hat und wieder schliesst. Die ganze Skala steht in
     `tokens.css`; der Vergleich ist dort abzulesen und nicht hier zu raten. */
  z-index: var(--z-fussleiste);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1.5rem;
  padding: 0 clamp(0.75rem, 3vw, 2rem);

  background: var(--footer-grund);
  border-top: 1px solid color-mix(in srgb, var(--footer-tinte) 18%, transparent);

  font-family: var(--schrift-mono);
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  /* Beiwerk bleibt zurueckgenommen -- es ist Dekor. */
  color: color-mix(in srgb, var(--footer-tinte) 62%, transparent);
  white-space: nowrap;
}

.site-footer__legal {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Der Stand ist keine Zier, sondern eine Auskunft: er steht etwas klarer als
   das uebrige Beiwerk, ohne die Rechtslinks zu ueberholen. Ziffern in fester
   Breite, damit die Ecke beim Bump nicht zappelt. */
.site-footer__version {
  color: color-mix(in srgb, var(--footer-tinte) 80%, transparent);
  font-variant-numeric: tabular-nums;
}

/* Copyright in derselben vollen Farbe wie die Rechtslinks; nur der Name
   darin ist ein Link zurueck auf lukasbaumert.de, "©" und Jahr bleiben
   reiner Text. */
.site-footer__copyright {
  color: var(--footer-tinte);
}

/* Die Rechtslinks stehen voll da, nicht gedaempft: sie muessen leicht
   erkennbar sein, das ist der Zweck der Leiste. */
.site-footer__link {
  color: var(--footer-tinte);
  text-decoration: none;
  border-bottom: 1px solid color-mix(in srgb, var(--footer-tinte) 45%, transparent);
  padding-bottom: 1px;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.site-footer__link:hover,
.site-footer__link:focus-visible {
  color: var(--footer-akzent);
  border-bottom-color: var(--footer-akzent);
}

/* Auf schmalen Schirmen zaehlt nur, was rechtlich muss -- und der Stand.
   Der bleibt: wer auf dem Telefon nachsieht, welche Fassung da liegt, tut es
   gerade dann, wenn etwas nicht stimmt. */
@media (max-width: 34rem) {
  .site-footer__mark {
    display: none;
  }

  .site-footer {
    justify-content: center;
  }
}
</style>
